import { useEffect } from "react";
import { getCalc } from "../lib/api";

/**
 * A1-93 (8) — two tabs disagreed on whether a calculation was signed off
 * or locked: each tab holds its own copy of the calc doc, and only the tab
 * that did the sign-off/lock merged the result. When a tab regains focus
 * it now re-reads the calc (one GET) and merges the status fields only —
 * figures, inputs and review state are untouched, so nothing the operator
 * is looking at shifts under them except the status itself.
 */
export const CALC_STATUS_FIELDS = Object.freeze([
  "signoff_id", "signed_off_at", "signed_off_by",
  "frozen", "frozen_at", "filing_reference",
  "superseded_by_calc_id", "superseded_at",
]);

export const mergeCalcStatus = (prev, fresh) => {
  if (!prev || !fresh || prev.id !== fresh.id) return prev;
  const changed = CALC_STATUS_FIELDS.filter(
    (k) => k in fresh && JSON.stringify(fresh[k]) !== JSON.stringify(prev[k]));
  if (!changed.length) return prev;
  const next = { ...prev };
  for (const k of changed) next[k] = fresh[k];
  return next;
};

export const useCalcStatusRefresh = (calcId, setCalc) => {
  useEffect(() => {
    if (!calcId || typeof window === "undefined") return undefined;
    let cancelled = false;
    const refresh = async () => {
      if (document.visibilityState === "hidden") return;
      try {
        const fresh = await getCalc(calcId);
        if (!cancelled && fresh) setCalc((prev) => mergeCalcStatus(prev, fresh));
      } catch { /* offline / deleted — keep what is on screen */ }
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [calcId, setCalc]);
};
